import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import axios from 'axios';  // Import Axios for HTTP requests
import '../AddProperty/addProperty.css';  // Using the same CSS for a consistent look
import StandardQuantityPopup from '../AddProperty/StandardQuantityPopup';
import LoadingPopup from '../../../SharedView/LoadingPopup';
import PropertyDetailsSection from '../AddProperty/PropertyDetailsSection';
import CustomPriceSection from '../AddProperty/CustomPriceSection';

const bedroomOptions = [
    { value: 0, label: "Studio" },
    { value: 1, label: "1 Bedroom" },
    { value: 2, label: "2 Bedrooms" },
    { value: 3, label: "3 Bedrooms" },
    { value: 4, label: "4 Bedrooms" },
    { value: 5, label: "5 Bedrooms" },
    { value: 6, label: "6 Bedrooms" },
    { value: 7, label: "7 Bedrooms" },
    { value: 8, label: "8 Bedrooms" }
];

const cleanerOptions = [
    { value: 1, label: "1 Cleaner" },
    { value: 2, label: "2 Cleaners" },
    { value: 3, label: "3 Cleaners" },
    { value: 4, label: "4 Cleaners" }
];

const bathroomOptions = [
    { value: 1, label: "1 Bathroom" },
    { value: 1.5, label: "1.5 Bathrooms" },
    { value: 2, label: "2 Bathrooms" },
    { value: 2.5, label: "2.5 Bathrooms" },
    { value: 3, label: "3 Bathrooms" },
    { value: 3.5, label: "3.5 Bathrooms" },
    { value: 4, label: "4 Bathrooms" },
    { value: 4.5, label: "4.5 Bathrooms" },
    { value: 5, label: "5 Bathrooms" },
    { value: 5.5, label: "5.5 Bathrooms" },
    { value: 6, label: "6 Bathrooms" },
    { value: 6.5, label: "6.5 Bathrooms" },
    { value: 7, label: "7 Bathrooms" },
    { value: 7.5, label: "7.5 Bathrooms" },
    { value: 8, label: "8 Bathrooms" }
];

const bedCountOptions = [
    { value: 0, label: "0 Bed" },
    { value: 1, label: "1 Bed" },
    { value: 2, label: "2 Beds" },
    { value: 3, label: "3 Beds" },
    { value: 4, label: "4 Beds" },
    { value: 5, label: "5 Beds" },
    { value: 6, label: "6 Beds" },
    { value: 7, label: "7 Beds" },
    { value: 8, label: "8 Beds" }
];

function EditProperty() {
    const { propertyId } = useParams();
    const [propertyData, setPropertyData] = useState({
        client_id: '',
        address_line_1: '',
        unit_number: '',
        bedrooms: '',
        bathrooms: '',
        area: '',
        google_maps_link: '',
        latitude: null,
        longitude: null,
        google_place_id: null,
        google_place_desc: '',
        is_linen_hire: false,
        is_beach_towel: false,
        is_custom_price: false,
        is_custom_sq: false,
        custom_price: '',
        required_cleaners: 1,
        permanent_notes: '',
        warehouse_note: '',
        scheduling_note: '',
        is_id_required: false,
        is_company_id_required: false,
        is_accompany_id_required: false,
        is_permit_needed: false,
        dissapearing_note: '',
        property_access: '',
        wifi_name: '',
        wifi_password: '',
        single_beds: 0,
        double_beds: 1,
        super_king_beds: 0,
        is_active: true,
        is_dolce_gusto: false,
        is_nespresso: false,
        standard_quantities: [],
    });

    const [clientDisplayName, setClientDisplayName] = useState("Loading...");
    const [showCustomSQPopup, setShowCustomSQPopup] = useState(false);

    // Multi-billing: which entity this property bills to. Only rendered when
    // the property's client has multi-billing enabled.
    const [billingProfiles, setBillingProfiles] = useState(null); // GET /clients/:id/billing-profiles payload
    const [billingSaving, setBillingSaving] = useState(false);
    const [billingMessage, setBillingMessage] = useState('');

    const [isLoading, setIsLoading] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [shouldNavigate, setShouldNavigate] = useState(false);
    const [activationMessage, setActivationMessage] = useState('');

    const navigate = useNavigate();

    useEffect(() => {
        // Fetch property data to populate the fields
        const fetchProperty = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get(`/api/properties/${propertyId}`);
                const data = response.data;
                setPropertyData((prev) => ({
                    ...prev,
                    ...data,
                    is_active:
                        typeof data.is_active === 'boolean' ? data.is_active : true,
                }));
                setClientDisplayName(data.client_display_name);
            } catch (error) {
                console.error('Error fetching property:', error);
                setFeedbackMessage('Failed to load property details.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchProperty();
    }, [propertyId]);

    // Multi-billing: load the client's billing entities once we know the client.
    useEffect(() => {
        const clientId = propertyData.client_id;
        if (!clientId) return;

        let cancelled = false;
        axios.get(`/api/clients/${clientId}/billing-profiles`)
            .then((res) => { if (!cancelled) setBillingProfiles(res.data); })
            .catch((err) => {
                console.error('Error fetching billing profiles:', err);
                if (!cancelled) setBillingProfiles(null);
            });
        return () => { cancelled = true; };
    }, [propertyData.client_id]);

    const handleBillingProfileChange = async (selectedOption) => {
        const newProfileId = selectedOption?.value ?? null; // null = default entity
        const previousId = propertyData.billing_profile_id ?? null;
        if (newProfileId === previousId) return;

        setBillingSaving(true);
        setBillingMessage('');
        try {
            await axios.put(`/api/properties/${propertyId}/billing-profile`, {
                billing_profile_id: newProfileId,
            });
            setPropertyData((prev) => ({ ...prev, billing_profile_id: newProfileId }));
            setBillingMessage('Billing entity updated.');
            setTimeout(() => setBillingMessage(''), 6000);
        } catch (error) {
            console.error('Error updating billing entity:', error);
            setBillingMessage(error.response?.data?.error || 'Failed to update billing entity.');
        } finally {
            setBillingSaving(false);
        }
    };

    // ✅ Load saved Standard Quantities for this property (so CustomPriceSection works on Edit)
    useEffect(() => {
        if (!propertyId) return;

        const fetchSQ = async () => {
            try {
                const res = await axios.get(`/api/properties/property-sq/${propertyId}`);
                const saved = res.data || [];

                setPropertyData((prev) => ({
                    ...prev,
                    standard_quantities: Array.isArray(saved) ? saved : [],
                }));
            } catch (err) {
                console.error("Error fetching property SQ:", err);
                // keep empty if it fails
                setPropertyData((prev) => ({
                    ...prev,
                    standard_quantities: Array.isArray(prev.standard_quantities)
                        ? prev.standard_quantities
                        : [],
                }));
            }
        };

        fetchSQ();
    }, [propertyId]);


    useEffect(() => {
        setPropertyData((pd) =>
            !pd.is_linen_hire && pd.is_beach_towel
                ? { ...pd, is_beach_towel: false }
                : pd // no-op return avoids extra render
        );
    }, [propertyData.is_linen_hire, propertyData.is_beach_towel]);

    const handleToggleActive = async () => {
        const newStatus = !propertyData.is_active;

        try {
            const response = await axios.patch(`/api/properties/${propertyId}/active`, {
                is_active: newStatus,
            });

            if (response.status === 200) {
                setPropertyData((prev) => ({ ...prev, is_active: newStatus }));
                setActivationMessage(
                    newStatus
                        ? 'Property activated. Client was also activated if it was archived.'
                        : 'Property archived successfully.'
                );
                setTimeout(() => setActivationMessage(''), 6000);
            } else {
                setActivationMessage('Error updating property status.');
            }
        } catch (error) {
            console.error('Error toggling property active status:', error);
            setActivationMessage('Error updating property status.');
        }
    };

    const handleSaveProperty = async (e) => {
        e.preventDefault();

        // Same rule as adding: the pin is mandatory, the place id is not.
        // This is also the screen the eleven location-less properties will be
        // fixed through, so it must refuse to save them still empty.
        // Same test the client-side property modals use (isValidCoord): a
        // coordinate must be finite AND meaningfully non-zero. `0` alone is
        // finite, and 0,0 is the Gulf of Guinea — which is exactly what the
        // eleven location-less properties are stored as.
        const isValidCoord = (n) => Number.isFinite(n) && Math.abs(n) > 0.000001;
        const lat = Number(propertyData.latitude);
        const lng = Number(propertyData.longitude);
        if (!isValidCoord(lat) || !isValidCoord(lng)) {
            setFeedbackMessage(
                'Pick the building on the map before saving — search for it, or '
                + 'click the map to drop a pin. Drivers navigate to that pin.'
            );
            return;
        }

        setIsLoading(true);
        try {
            const response = await axios.put(`/api/properties/${propertyId}`, propertyData);
            if (response.status === 200) {
                setFeedbackMessage('Property updated successfully.');
                setShouldNavigate(true); // Mark navigation as allowed
            } else {
                setFeedbackMessage('Failed to update property.');
            }
        } catch (error) {
            console.error('Error updating property:', error);

            const backendError = error.response?.data;

            // For any 400 from backend (Zoho / our wrapper), show its message
            if (error.response?.status === 400 && backendError?.message) {
                setFeedbackMessage(`${backendError.message}. Please contact a manager.`);
            } else {
                setFeedbackMessage('Failed to update property.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleBedroomChange = (selectedOption) => {
        const selectedBedrooms = selectedOption.value;
        let doubleBeds = 1; // Default to 1 double bed, including for Studio
        let cleaners = 1;    // Default 1 cleaner

        // Adjust double beds and cleaners based on bedroom count
        if (selectedBedrooms === 2) {
            doubleBeds = 2;
        } else if (selectedBedrooms >= 3) {
            doubleBeds = selectedBedrooms; // 3 or more bedrooms
            cleaners = 2; // For 3 or more bedrooms, 2 cleaners
        }

        setPropertyData({
            ...propertyData,
            bedrooms: selectedBedrooms,
            double_beds: doubleBeds,
            required_cleaners: cleaners
        });
    };

    const handleEditCustomSQ = () => {
        setShowCustomSQPopup(true);
    };

    const handleCloseSQPopup = () => {
        setShowCustomSQPopup(false);
    };

    const handleClosePopup = () => {
        if (shouldNavigate) {
            navigate(`/properties/${propertyId}`);
        }
        setFeedbackMessage(""); // Reset feedback message
    };

    return (
        <div className="add-property-container">
            <LoadingPopup
                isLoading={isLoading}
                feedbackMessage={feedbackMessage}
                onClose={handleClosePopup}
            />
            <div className="edit-property-header">
                <h2>Edit Property</h2>
                <div className="form-group is-active-toggle">
                    <label>
                        {propertyData.is_active ? 'Active' : 'Archived'}
                    </label>
                    <label className="switch">
                        <input
                            type="checkbox"
                            checked={propertyData.is_active ?? true}
                            onChange={handleToggleActive}
                        />
                        <span className="slider"></span>
                    </label>
                    {activationMessage && (
                        <div className="activation-message">
                            {activationMessage}
                        </div>
                    )}
                </div>
            </div>

            <form className="new-property-form" onSubmit={handleSaveProperty}>
                {/* Display client name but disable input */}
                <div className="form-section">
                    <h3>Client</h3>
                    <div className="form-group">
                        <label>Client Name</label>
                        <div
                            style={{
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                backgroundColor: '#f5f5f5',
                                cursor: 'not-allowed'
                            }}
                        >
                            {clientDisplayName}
                        </div>
                    </div>

                    {/* Multi-billing: pick which entity this property bills to.
                        Saves immediately (separate endpoint from the property PUT). */}
                    {billingProfiles?.is_multi_billing && (
                        <div className="form-group" style={{ marginTop: '12px' }}>
                            <label>Billed To</label>
                            <Select
                                options={[
                                    { value: null, label: `Default (${billingProfiles.default_entity?.label || clientDisplayName})` },
                                    ...(billingProfiles.profiles || []).map((p) => ({
                                        value: p.id,
                                        label: p.label || p.legal_name,
                                    })),
                                ]}
                                value={(() => {
                                    const current = propertyData.billing_profile_id ?? null;
                                    if (current == null) {
                                        return { value: null, label: `Default (${billingProfiles.default_entity?.label || clientDisplayName})` };
                                    }
                                    const match = (billingProfiles.profiles || []).find((p) => p.id === current);
                                    return match
                                        ? { value: match.id, label: match.label || match.legal_name }
                                        : { value: current, label: `Entity #${current}` };
                                })()}
                                onChange={handleBillingProfileChange}
                                isDisabled={billingSaving}
                                placeholder="Select billing entity"
                            />
                            {billingMessage && (
                                <div style={{ marginTop: '8px', fontSize: '13px', color: billingMessage.startsWith('Billing entity updated') ? '#16a34a' : '#b91c1c' }}>
                                    {billingMessage}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <PropertyDetailsSection
                    newProperty={propertyData}
                    setNewProperty={setPropertyData}
                />

                <div className="form-section">
                    <h3>Bedroom Configuration</h3>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Bedrooms</label>
                            <Select
                                options={bedroomOptions}
                                value={bedroomOptions.find(option => option.value === propertyData.bedrooms)}
                                onChange={handleBedroomChange}
                                placeholder="Select"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Bathrooms</label>
                            <Select
                                options={bathroomOptions}
                                value={bathroomOptions.find(option => option.value === propertyData.bathrooms)}
                                onChange={(selectedOption) => setPropertyData({ ...propertyData, bathrooms: selectedOption.value })}
                                placeholder="Select"
                                required
                            />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Single Beds</label>
                            <Select
                                options={bedCountOptions}
                                value={bedCountOptions.find(option => option.value === propertyData.single_beds)}
                                onChange={(selectedOption) => setPropertyData({ ...propertyData, single_beds: selectedOption.value })}
                                placeholder="Select Single Beds"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Double Beds</label>
                            <Select
                                options={bedCountOptions}
                                value={bedCountOptions.find(option => option.value === propertyData.double_beds)}
                                onChange={(selectedOption) => setPropertyData({ ...propertyData, double_beds: selectedOption.value })}
                                placeholder="Select Double Beds"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Super King Beds</label>
                            <Select
                                options={bedCountOptions}
                                value={bedCountOptions.find(option => option.value === (propertyData.super_king_beds || 0)) || bedCountOptions[0]}
                                onChange={(selectedOption) => setPropertyData({ ...propertyData, super_king_beds: selectedOption.value })}
                                placeholder="Select Super King Beds"
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section property-services-section">
                    <h3>Service, Pricing & Amenities</h3>

                    {/* Row 1: Linen, Beach Towel, Custom Price, Staffing */}
                    <div className="form-row property-services-row">

                        {/* Standard staffing */}
                        <div className="form-group property-select-group">
                            <label className="property-toggle-label">Standard Staffing</label>
                            <Select
                                options={cleanerOptions}
                                value={cleanerOptions.find(
                                    (option) =>
                                        option.value === propertyData.required_cleaners
                                )}
                                onChange={(selectedOption) =>
                                    setPropertyData({
                                        ...propertyData,
                                        required_cleaners: selectedOption.value,
                                    })
                                }
                                placeholder="Select"
                                required
                            />
                        </div>

                        {/* Linen Hire */}
                        <div className="form-group property-toggle-group">
                            <label className="property-toggle-label">Linen Hire</label>
                            <div className="property-toggle-inline">
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={propertyData.is_linen_hire}
                                        onChange={(e) =>
                                            setPropertyData({
                                                ...propertyData,
                                                is_linen_hire: e.target.checked,
                                            })
                                        }
                                    />
                                    <span className="slider"></span>
                                </label>
                            </div>
                        </div>

                        {/* Beach towel (only if LH enabled) */}
                        {propertyData.is_linen_hire && (
                            <div className="form-group property-toggle-group">
                                <label className="property-toggle-label">LH Beach Towel</label>
                                <div className="property-toggle-inline">
                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={propertyData.is_beach_towel}
                                            onChange={(e) =>
                                                setPropertyData({
                                                    ...propertyData,
                                                    is_beach_towel: e.target.checked,
                                                })
                                            }
                                        />
                                        <span className="slider"></span>
                                    </label>
                                </div>
                            </div>
                        )}

                        {/* Custom Laundry Quantity */}
                        <div className="form-group property-toggle-group">
                            <label className="property-toggle-label">
                                Custom Laundry Quantity
                            </label>
                            <div className="property-toggle-inline">
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={propertyData.is_custom_sq}
                                        onChange={(e) =>
                                            setPropertyData({
                                                ...propertyData,
                                                is_custom_sq: e.target.checked,
                                            })
                                        }
                                    />
                                    <span className="slider"></span>
                                </label>
                                {propertyData.is_custom_sq && (
                                    <span
                                        className="edit-custom-sq-link"
                                        onClick={handleEditCustomSQ}
                                    >
                                        Edit Custom Quantity
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Row 2: Custom SQ + Coffee amenities */}
                    <div className="form-row property-services-row">

                        {/* Coffee amenities, next to custom SQ */}
                        <div className="form-group property-amenities-group">
                            <label className="property-toggle-label">
                                Coffee Capsules Amenities
                            </label>
                            <div className="property-amenities-inline">
                                <div className="property-toggle-group small">
                                    <span className="property-toggle-label-sm">
                                        Nescafe Dolce Gusto
                                    </span>
                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={!!propertyData.is_dolce_gusto}
                                            onChange={(e) =>
                                                setPropertyData({
                                                    ...propertyData,
                                                    is_dolce_gusto: e.target.checked,
                                                })
                                            }
                                        />
                                        <span className="slider"></span>
                                    </label>
                                </div>

                                <div className="property-toggle-group small">
                                    <span className="property-toggle-label-sm">
                                        Nespresso
                                    </span>
                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={!!propertyData.is_nespresso}
                                            onChange={(e) =>
                                                setPropertyData({
                                                    ...propertyData,
                                                    is_nespresso: e.target.checked,
                                                })
                                            }
                                        />
                                        <span className="slider"></span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Custom price & calculator */}
                        <CustomPriceSection
                            propertyData={propertyData}
                            setPropertyData={setPropertyData}
                        />

                    </div>

                    <div className="form-group">
                        <label>Permanent Notes</label>
                        <textarea
                            value={propertyData.permanent_notes}
                            onChange={(e) => setPropertyData({ ...propertyData, permanent_notes: e.target.value })}
                            placeholder="Permanent notes for this property only for every visit"
                        />
                    </div>

                    <div className="form-group">
                        <label>Warehouse Note</label>
                        <textarea
                            value={propertyData.warehouse_note || ''}
                            onChange={(e) => setPropertyData({ ...propertyData, warehouse_note: e.target.value })}
                            placeholder="Notes for warehouse team (e.g., linen specifics, packing reminders)"
                        />
                    </div>

                    <div className="form-group">
                        <label>Note for Next Cleaning</label>
                        <textarea
                            value={propertyData.dissapearing_note || ''}
                            onChange={(e) => setPropertyData({ ...propertyData, dissapearing_note: e.target.value })}
                            placeholder="Temporary note that should be shown on the next cleaning"
                        />
                    </div>

                    <div className="form-group">
                        <label>Scheduling Note</label>
                        <textarea
                            value={propertyData.scheduling_note || ''}
                            onChange={(e) =>
                                setPropertyData({
                                    ...propertyData,
                                    scheduling_note: e.target.value,
                                })
                            }
                            placeholder="Internal scheduling remarks for this property"
                        />
                    </div>

                    {/* Scheduling access requirement toggles */}
                    <div className="form-row scheduling-toggles-row">
                        <div className="form-group">
                            <label className="property-toggle-label">ID Required</label>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={!!propertyData.is_id_required}
                                    onChange={(e) =>
                                        setPropertyData({
                                            ...propertyData,
                                            is_id_required: e.target.checked,
                                        })
                                    }
                                />
                                <span className="slider"></span>
                            </label>
                        </div>

                        <div className="form-group">
                            <label className="property-toggle-label">Company ID Required</label>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={!!propertyData.is_company_id_required}
                                    onChange={(e) =>
                                        setPropertyData({
                                            ...propertyData,
                                            is_company_id_required: e.target.checked,
                                        })
                                    }
                                />
                                <span className="slider"></span>
                            </label>
                        </div>

                        <div className="form-group">
                            <label className="property-toggle-label">Accompany ID Required</label>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={!!propertyData.is_accompany_id_required}
                                    onChange={(e) =>
                                        setPropertyData({
                                            ...propertyData,
                                            is_accompany_id_required: e.target.checked,
                                        })
                                    }
                                />
                                <span className="slider"></span>
                            </label>
                        </div>

                        <div className="form-group">
                            <label className="property-toggle-label">Permit Needed</label>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={!!propertyData.is_permit_needed}
                                    onChange={(e) =>
                                        setPropertyData({
                                            ...propertyData,
                                            is_permit_needed: e.target.checked,
                                        })
                                    }
                                />
                                <span className="slider"></span>
                            </label>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Property Access Info</label>
                            <input
                                type="text"
                                value={propertyData.property_access}
                                onChange={(e) => setPropertyData({ ...propertyData, property_access: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label>Wi-Fi Name</label>
                            <input
                                type="text"
                                value={propertyData.wifi_name}
                                onChange={(e) => setPropertyData({ ...propertyData, wifi_name: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label>Wi-Fi Password</label>
                            <input
                                type="text"
                                value={propertyData.wifi_password}
                                onChange={(e) => setPropertyData({ ...propertyData, wifi_password: e.target.value })}
                            />
                        </div>
                    </div>
                </div>

                <button type="submit" className="submit-button">Save Changes</button>
            </form>
            {/* Render the popup when the state is true */}
            {showCustomSQPopup && (
                <StandardQuantityPopup
                    propertyId={propertyId}
                    initialQuantities={propertyData.standard_quantities}   // ✅ important
                    bedrooms={propertyData.bedrooms}
                    singleBeds={propertyData.single_beds}
                    doubleBeds={propertyData.double_beds}
                    superKingBeds={propertyData.super_king_beds}
                    isBeachTowel={!!propertyData.is_linen_hire && !!propertyData.is_beach_towel}
                    onSave={(rows) =>                                  // ✅ important
                        setPropertyData((prev) => ({
                            ...prev,
                            standard_quantities: Array.isArray(rows) ? rows : [],
                        }))
                    }
                    onClose={handleCloseSQPopup}
                />
            )}
        </div>
    );
}

export default EditProperty;